import { useCallback } from "react";
import { FIELDS } from "./fields";
import { useServerState } from "../utils/useServerState";

// Which table columns a user sees, and in what order. Saved per user by the
// API, so the layout follows the user to any browser. Channel name is always
// shown and always first.

const DEFAULT_HIDDEN = ["revshare", "revshareType"];
const PINNED = "channelName";

const defaults = () => ({
  order: FIELDS.map((f) => f.key),
  hidden: DEFAULT_HIDDEN,
});

// Fits a saved layout to the current field list: unknown keys dropped, fields
// added since it was saved put right after the field before them (shown).
function normalize(saved) {
  const known = new Set(FIELDS.map((f) => f.key));
  const order = (saved.order || []).filter((k) => known.has(k) && k !== PINNED);
  FIELDS.forEach((f, i) => {
    if (f.key === PINNED || order.includes(f.key)) return;
    const after = order.indexOf(FIELDS[i - 1]?.key);
    order.splice(after + 1, 0, f.key);
  });
  return {
    order: [PINNED, ...order],
    hidden: (saved.hidden || []).filter((k) => known.has(k) && k !== PINNED),
  };
}

// null until the user first changes a column
const fromServer = (saved) => normalize(saved && typeof saved === "object" ? saved : defaults());

export function useColumnPrefs(userKey) {
  const [prefs, setPrefs] = useServerState("/me/columns", userKey, normalize(defaults()), fromServer);

  const setVisible = useCallback((key, visible) => {
    if (key === PINNED) return;
    setPrefs((p) => ({
      ...p,
      hidden: visible ? p.hidden.filter((k) => k !== key) : [...new Set([...p.hidden, key])],
    }));
  }, [setPrefs]);

  // Moves `key` to where `targetKey` is now
  const move = useCallback((key, targetKey) => {
    if (key === PINNED || targetKey === PINNED || key === targetKey) return;
    setPrefs((p) => {
      const order = p.order.filter((k) => k !== key);
      const at = order.indexOf(targetKey) + (p.order.indexOf(key) < p.order.indexOf(targetKey) ? 1 : 0);
      order.splice(at, 0, key);
      return { ...p, order };
    });
  }, [setPrefs]);

  const showAll = useCallback(() => setPrefs((p) => ({ ...p, hidden: [] })), [setPrefs]);
  const reset = useCallback(() => setPrefs(normalize(defaults())), [setPrefs]);
  // A saved view brings its own column layout
  const applyLayout = useCallback((layout) => setPrefs(normalize(layout || defaults())), [setPrefs]);

  const fieldByKey = Object.fromEntries(FIELDS.map((f) => [f.key, f]));
  const ordered = prefs.order.map((k) => fieldByKey[k]);
  const visible = ordered.filter((f) => !prefs.hidden.includes(f.key));

  return {
    ordered,
    visible,
    hidden: prefs.hidden,
    layout: prefs,
    setVisible,
    move,
    showAll,
    reset,
    applyLayout,
    pinned: PINNED,
  };
}
